import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteUserAlias } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { User } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteUserAliasDialogProps {
  user: User;
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  userEmail: string;
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DeleteUserAliasDialog({
  open,
  handleClose,
  selectedRows,
  userEmail,
  user,
  changeLogRoles,
  setSelectedRows,
}: DeleteUserAliasDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteUserAlias(
        userEmail,
        user.aliases?.length || 0,
        'User',
        selectedRows,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: selectedRows.length > 25 ? 'info' : 'success',
        message:
          selectedRows.length > 25
            ? 'The task to delete the user alias(es) has begun.'
            : 'User alias(es) deleted successfully!',
      });

      if (selectedRows.length <= 25) {
        const currentAliases = user.aliases || [];
        const newList = currentAliases.filter(
          (alias) => !selectedRows.includes(alias)
        );

        queryClient.setQueryData([UserKeys.User, user.primaryEmail], {
          ...user,
          aliases: newList,
        });
      }
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting a user alias(es). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.aliases.delete_user_aliases,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.aliases,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.RemoveAliases.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Alias"
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedRows.length > 1 ? (
              <>
                Delete <strong>{selectedRows.length} aliases</strong>?
              </>
            ) : (
              <>
                Delete alias <strong>{selectedRows[0]}</strong>?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Alias',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
